import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';
import { getImageURL } from '../utils/api';
import NotificationService from '../services/notificationService';

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  height: 64px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb; /* gray-200 */
  background: #ffffff;
  padding: 0 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  color: #4b5563; /* gray-600 */
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #f3f4f6; color: #111827; }
`;

const Input = styled.input`
  width: 256px;
  border: 1px solid #d1d5db; /* gray-300 */
  background: #f9fafb;
  border-radius: 8px;
  padding: 8px 12px 8px 36px;
  font-size: 14px;
  outline: none;
  &:focus { border-color: #6366f1; box-shadow: 0 0 0 2px rgba(99,102,241,0.2); }
`;

const Dropdown = styled.div`
  position: absolute;
  right: 0;
  margin-top: 8px;
  width: 224px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1);
`;

const Row = styled.div`
  padding: 10px 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const MenuBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  &:hover { background: #f3f4f6; }
`;

const RoleBadge = styled.span`
  display: inline-block;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #d1d5db;
`;

const NotifDropdown = styled.div`
  position: absolute;
  right: 0;
  margin-top: 8px;
  width: 320px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 10px 25px -5px rgba(0,0,0,0.12), 0 4px 10px -5px rgba(0,0,0,0.08);
  z-index: 50;
  overflow: hidden;
`;

const NotifBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 16px;
  height: 16px;
  border-radius: 9999px;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
  pointer-events: none;
`;

const NotifItem = styled.button`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border: none;
  border-bottom: 1px solid #f3f4f6;
  background: ${(p) => (p.$unread ? '#f5f3ff' : 'transparent')};
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
  &:hover { background: #f3f4f6; }
  &:last-of-type { border-bottom: none; }
`;

/** Format an ISO timestamp as a relative time string. */
function relativeTime(isoString) {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

function mergeNotifications(existing, incoming) {
  return NotificationService.mergeNotificationLists(existing, incoming);
}

function fmt12h(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  let hours = parseInt(h, 10);
  const mod = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${m} ${mod}`;
}

/** Build a single notification for today's unmarked-attendance sessions. */
function buildUnmarkedAttendanceNotification(schedules) {
  if (!schedules || schedules.length === 0) return [];

  const courseCounts = {};
  schedules.forEach((s) => {
    const k = s.courseName || 'Uncategorized';
    courseCounts[k] = (courseCounts[k] || 0) + 1;
  });

  const courseSummary = Object.entries(courseCounts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([course, count]) => `${course}: ${count}`)
    .join(' · ');

  return [
    {
      id: 'unmarked-attendance-summary',
      type: 'warning',
      title: `🔴 ${schedules.length} class${schedules.length > 1 ? 'es' : ''} with unmarked attendance today`,
      message: `Attendance was not recorded for ended sessions. ${courseSummary}`,
      link: '/admin/notifications/unmarked-attendance',
      isRead: false,
      createdAt: new Date().toISOString(),
    },
  ];
}

/** Build local notification objects from stale ticket data. */
function buildStaleNotifications(overdueOpen, overdueInProgress) {
  const totalOpen = overdueOpen.length;
  const totalInProgress = overdueInProgress.length;
  const total = totalOpen + totalInProgress;

  if (total === 0) return [];

  // Build a compact course-wise summary line
  const courseCounts = {};
  overdueOpen.forEach((t) => {
    const k = t.courseName || 'Uncategorized';
    if (!courseCounts[k]) courseCounts[k] = { open: 0, inProgress: 0 };
    courseCounts[k].open += 1;
  });
  overdueInProgress.forEach((t) => {
    const k = t.courseName || 'Uncategorized';
    if (!courseCounts[k]) courseCounts[k] = { open: 0, inProgress: 0 };
    courseCounts[k].inProgress += 1;
  });

  const courseSummary = Object.entries(courseCounts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([course, { open, inProgress }]) => {
      const parts = [];
      if (open > 0) parts.push(`${open} open`);
      if (inProgress > 0) parts.push(`${inProgress} in-progress`);
      return `${course}: ${parts.join(', ')}`;
    })
    .join(' · ');

  const parts = [];
  if (totalOpen > 0) parts.push(`${totalOpen} open >3 days`);
  if (totalInProgress > 0) parts.push(`${totalInProgress} in-progress >7 days`);

  return [
    {
      id: 'stale-issues-summary',
      type: 'warning',
      title: `⚠️ ${total} overdue student issue${total > 1 ? 's' : ''} require attention`,
      message: `${parts.join(' · ')}. ${courseSummary}`,
      link: '/admin/notifications/stale-issues',
      isRead: false,
      createdAt: new Date().toISOString(),
    },
  ];
}

export default function TopBar({ title, onMenuClick, sidebarOpen }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const staleNotifIdsRef = useRef(new Set());
  const unmarkedNotifIdRef = useRef(false); // true when the notif is injected
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const stored = AuthService.getUser();
  const userId = stored?.superAdmin?.superAdminId;
  const staff = stored?.superAdmin?.staff;
  const staffRoleRaw = staff?.staffType || staff?.role || staff?.position || staff?.designation || staff?.type || '';
  const normalizedRole = String(staffRoleRaw).trim().toLowerCase();
  const isSuperAdmin = Boolean(stored?.superAdmin?.superAdminId);
  const isAdminRole = normalizedRole === 'admin' || normalizedRole === 'administrator';
  const isManagementRole = normalizedRole === 'management' || normalizedRole === 'manager';
  const canViewSalesAdmin = isSuperAdmin || isAdminRole;
  const canViewRecovery = isSuperAdmin || isAdminRole || isManagementRole;

  // ── Live notifications (admin only) ────────────────────────────────────────
  const loadNotifications = useCallback(async () => {
    if (!userId || !isAdminRole) return;
    setNotifLoading(true);
    try {
      const data = await NotificationService.fetchNotifications(userId);
      setNotifications((prev) => mergeNotifications(prev, data));
    } finally {
      setNotifLoading(false);
    }
  }, [userId, isAdminRole]);

  useEffect(() => {
    if (!userId || !isAdminRole) return;
    const cached = NotificationService.readLocalNotifications(userId);
    if (cached.length > 0) {
      setNotifications((prev) => mergeNotifications(prev, cached));
    }
  }, [userId, isAdminRole]);

  useEffect(() => {
    const handler = (event) => {
      const notif = event.detail;
      if (!notif || !userId || !isAdminRole) return;
      NotificationService.saveLocalNotification(userId, notif);
      loadNotifications();
    };

    window.addEventListener('kaaryaalaya:notif-local', handler);
    return () => window.removeEventListener('kaaryaalaya:notif-local', handler);
  }, [userId, isAdminRole, loadNotifications]);

  // Re-fetch when another component (e.g. Timetable.jsx) signals a new notification
  useEffect(() => {
    if (!isAdminRole) return;
    const handler = () => loadNotifications();
    window.addEventListener('kaaryaalaya:notif-refresh', handler);
    return () => window.removeEventListener('kaaryaalaya:notif-refresh', handler);
  }, [isAdminRole, loadNotifications]);

  // Poll every 30 s as a Socket.IO fallback (catches notifications from any source)
  useEffect(() => {
    if (!userId || !isAdminRole) return;
    const timer = setInterval(loadNotifications, 30_000);
    return () => clearInterval(timer);
  }, [userId, isAdminRole, loadNotifications]);

  useEffect(() => {
    if (!userId || !isAdminRole) return;
    NotificationService.connect(userId);
    loadNotifications();

    const offNew = NotificationService.onNewNotification((notif) => {
      setNotifications((prev) => mergeNotifications(prev, [notif]));
    });
    const offRead = NotificationService.onNotificationRead(({ id }) => {
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, isRead: true } : n));
    });
    const offAll = NotificationService.onAllRead(() => {
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    });
    const offDel = NotificationService.onNotificationDeleted(({ id }) => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    });

    return () => {
      offNew(); offRead(); offAll(); offDel();
      NotificationService.disconnect();
    };
  }, [userId, isAdminRole, loadNotifications]);

  // ── Stale-issue polling ──────────────────────────────────────────────────────
  const checkStaleIssues = useCallback(async () => {
    if (!isAdminRole) return;
    try {
      const resp = await axiosInstance.get('/tickets/stale');
      const { overdueOpen = [], overdueInProgress = [] } = resp?.data || {};
      const built = buildStaleNotifications(overdueOpen, overdueInProgress);

      const builtIds = new Set(built.map((n) => n.id));
      const prevIds = staleNotifIdsRef.current;

      setNotifications((prev) => {
        let next = prev.filter((n) => !prevIds.has(n.id) || builtIds.has(n.id));
        built.forEach((notif) => {
          const existing = next.find((n) => n.id === notif.id);
          if (!existing) {
            next = [notif, ...next];
          }
        });
        return next.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      });
      staleNotifIdsRef.current = builtIds;
    } catch (_) {
      // best-effort
    }
  }, [isAdminRole]);

  useEffect(() => {
    if (!isAdminRole) return;
    checkStaleIssues();
    const timer = setInterval(checkStaleIssues, 5 * 60_000);
    return () => clearInterval(timer);
  }, [isAdminRole, checkStaleIssues]);

  // ── Unmarked-attendance polling ───────────────────────────────────────────
  const checkUnmarkedAttendance = useCallback(async () => {
    if (!isAdminRole) return;
    try {
      const resp = await axiosInstance.get('/admin/attendance/unmarked-today');
      const schedules = Array.isArray(resp?.data) ? resp.data : [];
      const built = buildUnmarkedAttendanceNotification(schedules);
      const hasNew = built.length > 0;

      setNotifications((prev) => {
        // Remove old unmarked notif if classes are now all marked
        let next = prev.filter((n) => n.id !== 'unmarked-attendance-summary');
        if (hasNew) {
          const existing = prev.find((n) => n.id === 'unmarked-attendance-summary');
          // Preserve isRead state if already acknowledged
          next = [{ ...built[0], isRead: existing?.isRead ?? false }, ...next];
        }
        return next.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      });
      unmarkedNotifIdRef.current = hasNew;
    } catch (_) {
      // best-effort
    }
  }, [isAdminRole]);

  useEffect(() => {
    if (!isAdminRole) return;
    checkUnmarkedAttendance();
    const timer = setInterval(checkUnmarkedAttendance, 10 * 60_000); // every 10 min
    return () => clearInterval(timer);
  }, [isAdminRole, checkUnmarkedAttendance]);
  // ────────────────────────────────────────────────────────────────────────────

  const handleMarkAllRead = async () => {
    if (!userId) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    try { await NotificationService.markAllRead(userId); } catch (_) {}
  };

  const handleNotifClick = async (notif) => {
    if (!notif.isRead) {
      setNotifications((prev) => prev.map((n) => n.id === notif.id ? { ...n, isRead: true } : n));
      try { await NotificationService.markRead(notif.id, userId); } catch (_) {}
    }
    if (notif.link) window.location.href = notif.link;
  };
  // ────────────────────────────────────────────────────────────────────────────

  const getUserInfo = () => {
    if (!stored || !stored.superAdmin) {
      return { name: 'Guest User', email: 'guest@example.com', role: 'Guest', avatar: undefined };
    }
    const sa = stored.superAdmin;
    const staff = sa.staff;
    if (staff) {
      return {
        name: staff.name || 'Admin User',
        email: staff.officeEmail || staff.gmail || '',
        role: canViewSalesAdmin ? 'Admin' : (staff.type || 'Staff'),
        // use stored imagePath directly in img src
        avatar: staff.imagePath || undefined,
      };
    }
    return { name: 'User', email: '', role: 'User', avatar: undefined };
  };

  const user = getUserInfo();

  const handleSearch = (e) => {
    e.preventDefault();
    // placeholder search
    console.log('Searching for:', searchQuery);
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.post('/logout');
    } catch {}
    AuthService.clear();
    window.location.href = '/';
  };

  const roleColors = {
    Admin: { bg: 'linear-gradient(90deg,#f3e8ff,#fce7f3)', color: '#6b21a8', border: '#d8b4fe' },
  };
  const rc = roleColors[user.role] || { bg: 'linear-gradient(90deg,#f3f4f6,#e2e8f0)', color: '#1f2937', border: '#cbd5e1' };

  return (
    <Header className={sidebarOpen ? 'ml-0' : 'ml-0'}>
      <Left>
        <IconButton onClick={onMenuClick} aria-label="Toggle menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
        </IconButton>
        {title && <h1 style={{ fontSize: 18, fontWeight: 600, color: '#111827' }}>{title}</h1>}
        <nav aria-label="Breadcrumb" style={{ display: 'none' }}></nav>
      </Left>
      <Right>
        {/* Bell Notification Icon — admin only */}
        {isAdminRole && <div style={{ position: 'relative' }}>
          <IconButton
            onClick={() => {
              const opening = !showNotifications;
              setShowNotifications((v) => !v);
              setShowUserMenu(false);
              if (opening && unreadCount > 0) handleMarkAllRead();
            }}
            aria-label="Notifications"
            style={{ position: 'relative' }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
            </svg>
            {unreadCount > 0 && <NotifBadge>{unreadCount}</NotifBadge>}
          </IconButton>
          {showNotifications && (
            <NotifDropdown>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Notifications</span>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    style={{ fontSize: 12, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Mark all as read
                  </button>
                )}
              </div>
              {/* Notification list */}
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                {notifLoading ? (
                  <p style={{ padding: '24px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading…</p>
                ) : notifications.length === 0 ? (
                  <p style={{ padding: '24px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>No notifications</p>
                ) : (() => {
                  const unread = notifications.filter((n) => !n.isRead);
                  const read = notifications.filter((n) => n.isRead);
                  const renderNotif = (notif) => (
                    <NotifItem key={notif.id} $unread={!notif.isRead} onClick={() => handleNotifClick(notif)}>
                      {/* Type icon + unread indicator */}
                      <div style={{ flexShrink: 0, marginTop: 1, position: 'relative' }}>
                        {notif.type === 'warning' ? (
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            </svg>
                          </div>
                        ) : (
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: !notif.isRead ? '#dcfce7' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: !notif.isRead ? '#22c55e' : '#d1d5db' }} />
                          </div>
                        )}
                        {/* Green dot badge for unread warning-type */}
                        {!notif.isRead && notif.type === 'warning' && (
                          <span style={{ position: 'absolute', top: -2, right: -2, width: 9, height: 9, borderRadius: '50%', background: '#22c55e', border: '1.5px solid #fff' }} />
                        )}
                      </div>
                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13, fontWeight: !notif.isRead ? 700 : 500, color: '#111827', marginBottom: 3, lineHeight: 1.35 }}>{notif.title}</p>
                        <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.45, marginBottom: 6 }}>{notif.message}</p>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>{relativeTime(notif.createdAt)}</span>
                          {notif.link && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#6366f1', display: 'flex', alignItems: 'center', gap: 2 }}>
                              View details
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                            </span>
                          )}
                        </div>
                      </div>
                    </NotifItem>
                  );
                  return (
                    <>
                      {unread.length > 0 && (
                        <>
                          <div style={{ padding: '6px 16px 4px', display: 'flex', alignItems: 'center', gap: 6, background: '#f0fdf4', borderBottom: '1px solid #bbf7d0' }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block', flexShrink: 0 }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Unread · {unread.length}
                            </span>
                          </div>
                          {unread.map(renderNotif)}
                        </>
                      )}
                      {read.length > 0 && (
                        <>
                          <div style={{ padding: '6px 16px 4px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', borderTop: unread.length > 0 ? '2px solid #e5e7eb' : 'none' }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Earlier
                            </span>
                          </div>
                          {read.map(renderNotif)}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
              {/* See all */}
              <div style={{ borderTop: '1px solid #e5e7eb' }}>
                <button
                  onClick={() => { setShowNotifications(false); window.location.href = '/admin/notifications'; }}
                  style={{ width: '100%', padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'center', letterSpacing: 0.2 }}
                >
                  See all notifications
                </button>
              </div>
            </NotifDropdown>
          )}
        </div>}
        <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            <svg style={{ position: 'absolute', left: 12, top: 10 }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
          </div>
        </form>
        <div style={{ position: 'relative' }}>
          <IconButton onClick={() => { setShowUserMenu((v) => !v); setShowNotifications(false); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px' }}>
            {user.avatar && (
              <img
                src={getImageURL(user.avatar)}
                alt={user.name || 'User'}
                style={{ height: 32, width: 32, borderRadius: '9999px', objectFit: 'cover' }}
              />
            )}
            <span style={{ marginLeft: 8, fontSize: 14, color: '#374151', fontWeight: 500 }}>{user.name || 'User'}</span>
            <svg style={{ marginLeft: 4 }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
          </IconButton>
          {showUserMenu && (
            <Dropdown>
              <Row>
                <p style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{user.name}</p>
                <p style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>{user.email}</p>
                <RoleBadge style={{ marginTop: 8, background: rc.bg, color: rc.color, borderColor: rc.border }}>{user.role}</RoleBadge>
              </Row>
              <div>
                <MenuBtn onClick={() => (window.location.href = '/admin/profile')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                  Your Profile
                </MenuBtn>
                <MenuBtn onClick={() => (window.location.href = '/lecturer/dashboard')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                  Lecturer View
                </MenuBtn>
                {canViewSalesAdmin && (
                  <MenuBtn onClick={() => (window.location.href = '/admin-sales/dashboard')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                    View Sales Admin
                  </MenuBtn>
                )}
                {canViewRecovery && (
                  <MenuBtn onClick={() => (window.location.href = '/recovery/dashboard')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    View Due Recovery
                  </MenuBtn>
                )}
              </div>
              <div style={{ borderTop: '1px solid #e5e7eb' }}>
                <MenuBtn onClick={handleLogout} style={{ color: '#dc2626' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
                  Sign Out
                </MenuBtn>
              </div>
            </Dropdown>
          )}
        </div>
      </Right>
    </Header>
  );
}
